/** Notebook detail — /notebooks/:name (port of notebooks/detail.html,
 *  Phase 5 of the port).
 *
 * Two faces, exactly as the Jinja page had: a Results tab (game selector,
 * per-game downloads, the self-contained HTML full width in an iframe)
 * when generated outputs exist, and a Developer Center (download the
 * notebook pre-configured for a game, quickstart, template preview) —
 * the whole page when there are none. The active tab lives in the URL
 * hash (#results / #developer) so it can be linked and survives reload.
 *
 * Artifact addresses (viewer, downloads, preview, notebook download) come
 * from the payload, built server-side — the client never learns artifact
 * routing. The notebook download is a real form GET so the browser
 * follows the attachment response itself.
 *
 * Two deliberate departures from the Jinja page, both because the
 * /notebooks/ index it linked to is gone (the section folded into
 * Developer Tools, 2026-08-30): no "← Notebooks" back link, and the API
 * key "manage" link points at /developer-tools.
 */
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { API_BASE, api, ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/analytics'
import { pathSeg } from '../../lib/urls'
import './nb-detail.css'
import { withViewAs } from '../../lib/viewAs'

type Detail = {
  nb: { name: string; title: string; description?: string | null;
        standalone?: boolean }
  games: { game_code: string; id: number; match_date: string | null }[]
  gated: boolean
  results: { game: string; viewer: string | null;
             downloads: { label: string; url: string }[];
             generated_at: string | null }[]
  selected_game: string | null
  interactive_entries: { game: string; url: string }[]
  key_row: { prefix: string; created_at: string | null } | null
  preview_url: string
  download_url: string
}

const QUICKSTART = `git clone https://github.com/priorisports/sdk-notebooks.git
cd sdk-notebooks
python3 -m venv .venv
source .venv/bin/activate
pip install -r requirements.txt
export PRIORI_API_KEY="<your-priori-key>"
jupyter lab`

/** YYYY-MM-DD from an ISO timestamp, as the Jinja strftime rendered. */
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : null)

export default function NotebookDetail() {
  const { name = '' } = useParams<{ name: string }>()
  const [search] = useSearchParams()
  const [detail, setDetail] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'results' | 'developer'>(
    window.location.hash === '#developer' ? 'developer' : 'results')
  const [game, setGame] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const deepLink = search.get('game')
    const q = deepLink ? `?game=${pathSeg(deepLink)}` : ''
    api<Detail>(`/api/v1/notebooks/${pathSeg(name)}/detail${q}`)
      .then((d) => {
        setDetail(d)
        setGame(d.selected_game)
        // The Jinja notebooks index fired this on its card click; the
        // SPA's equivalent open is the detail landing (same name and
        // property, so the Mixpanel trend stays continuous).
        trackEvent('Notebook Opened', { notebook: d.nb.name })
        // The Jinja page ran showResult(selected_game) on load, so an
        // OPEN counted as a view — and deep-linked opens are exactly
        // the traffic the Insights recap links generate.
        if (d.selected_game) {
          trackEvent('Notebook Results Viewed',
                     { notebook: d.nb.name, game: d.selected_game })
        }
      })
      .catch((e: unknown) => setError(
        e instanceof ApiError && e.status === 404
          ? 'This notebook does not exist.'
          : 'The notebook could not be loaded.'))
    // The deep-link param only matters on first load, as on the Jinja page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  if (error) {
    return (
      <div className="nb-detail"><div className="nb-detail-container">
        <p style={{ color: '#6b7280' }}>{error}</p>
      </div></div>
    )
  }
  if (!detail) return null

  const hasResults = detail.results.length > 0
  const active = hasResults ? tab : 'developer'
  const result = detail.results.find((r) => r.game === game) ?? null

  const showTab = (next: 'results' | 'developer') => {
    setTab(next)
    // The tab is part of the URL, so it can be linked and survives reload.
    history.replaceState(null, '', `#${next}`)
  }

  const showResult = (code: string) => {
    setGame(code)
    trackEvent('Notebook Results Viewed', { notebook: detail.nb.name, game: code })
  }

  const copyQuickstart = () => {
    void navigator.clipboard.writeText(QUICKSTART).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
    trackEvent('Notebooks Quickstart Copied', { page: 'detail', notebook: detail.nb.name })
  }

  return (
    <div className="nb-detail">
      <div className="nb-detail-container">
        <h3 style={{ margin: '0 0 4px' }}>{detail.nb.title}</h3>
        {detail.nb.description && (
          <p style={{ color: '#6b7280', margin: 0, maxWidth: 760 }}>
            {detail.nb.description}
          </p>
        )}

        {hasResults && (
          <div className="nb-tabs" role="tablist">
            <button className={`nb-tab ${active === 'results' ? 'is-active' : ''}`}
                    role="tab" onClick={() => showTab('results')}>Results</button>
            <button className={`nb-tab ${active === 'developer' ? 'is-active' : ''}`}
                    role="tab" onClick={() => showTab('developer')}>Developer Center</button>
          </div>
        )}

        {/* Results: game selector + downloads, then the self-contained
            HTML full width. */}
        {hasResults && active === 'results' && (
          <div role="tabpanel">
            <div className="nb-results-bar">
              <select className="nb-select" value={game ?? ''}
                      onChange={(e) => showResult(e.target.value)}>
                {detail.results.map((r) => (
                  <option key={r.game} value={r.game}>
                    {r.game}{r.generated_at ? ` · ${day(r.generated_at)}` : ''}
                  </option>
                ))}
              </select>
              <span>
                {result?.downloads.map((d) => (
                  <a key={d.url} className="nb-download" href={`${API_BASE}${d.url}`}>
                    <i className="ph ph-download-simple" /> {d.label}
                  </a>
                ))}
              </span>
            </div>
            <div className="nb-result-pane">
              {result?.viewer && (
                <iframe src={withViewAs(`${API_BASE}${result.viewer}`, API_BASE)}
                        title={`${detail.nb.title} results`} />
              )}
            </div>
          </div>
        )}

        {/* Developer Center: download pre-configured, quick start, template
            preview. Second tab when there are results; the whole page when
            there are none. */}
        {active === 'developer' && (
          <div className="nb-dev">
            <div className="nb-dev-body">
              <div className="nb-dev-cols">
                <div>
                  <h6>Run it on your game</h6>
                  {detail.games.length > 0 ? (
                    <>
                      <form method="GET" action={`${API_BASE}${detail.download_url}`}
                            onSubmit={(e) => {
                              const sel = e.currentTarget.elements.namedItem('game')
                              trackEvent('Notebook Downloaded', {
                                notebook: detail.nb.name,
                                game: sel instanceof HTMLSelectElement ? sel.value : '',
                              })
                            }}>
                        <select name="game" className="nb-select"
                                style={{ fontSize: 14, marginBottom: 8 }}>
                          {detail.games.map((g) => (
                            <option key={g.game_code} value={g.game_code}>
                              {g.game_code}{g.match_date ? ` (${day(g.match_date)})` : ''}
                            </option>
                          ))}
                        </select>
                        <button type="submit" className="nb-btn"
                                style={{ width: '100%' }}>
                          Download notebook
                        </button>
                      </form>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
                        {detail.nb.standalone !== false
                          ? 'Pre-configured for the selected game. Run top to bottom.'
                          : <>Pre-pointed at the selected game. This notebook ships
                              with support files, so drop{' '}
                              <code>{detail.nb.name}.ipynb</code> into its
                              repo-clone folder and run from there.</>}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 14, color: '#6b7280' }}>
                      {detail.gated
                        ? 'No onboarded games for this notebook yet. Process a '
                          + "game through this notebook's pipeline to enable it here."
                        : 'No games available for your organization yet.'}
                    </div>
                  )}

                  {detail.interactive_entries.length > 0 && (
                    <>
                      <h6 style={{ marginTop: 20 }}>Interactive pages</h6>
                      {detail.interactive_entries.map((entry) => (
                        <a key={entry.game} className="nb-action" target="_blank"
                           rel="noopener noreferrer" href={`${API_BASE}${entry.url}`}
                           onClick={() => trackEvent('Notebook Interactive Viewed', {
                             notebook: detail.nb.name, game: entry.game,
                           })}>
                          <i className="ph ph-cursor-click" /> {entry.game}
                        </a>
                      ))}
                    </>
                  )}
                </div>
                <div>
                  <h6>Quick start</h6>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
                    {detail.key_row
                      ? <>API key <span className="key-prefix">{detail.key_row.prefix}...</span></>
                      : 'No notebooks API key yet.'}{' '}
                    <Link to="/developer-tools" style={{ color: '#2563eb' }}>manage</Link>
                  </div>
                  <div className="quickstart-block">
                    <button className="copy-btn" onClick={copyQuickstart}>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    <pre style={{ margin: 0, background: 'none', padding: 0 }}>
                      <code>{QUICKSTART}</code>
                    </pre>
                  </div>
                </div>
              </div>

              <div className="nb-preview-pane">
                <div className="nb-pane-head">Template preview</div>
                <iframe src={withViewAs(`${API_BASE}${detail.preview_url}`, API_BASE)}
                        title={`${detail.nb.title} template preview`} loading="lazy" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
